/**
 * Twitter / X admin API client — Phase 14 Plan 14-05 Task 1.
 *
 * Thin fetch wrappers around Plan 14-04's `/api/cms/admin/integrations/twitter/*`
 * super-admin endpoints. Auth rides the existing session cookie
 * (credentials: 'include') — the SuperAdminUser extractor on the backend
 * gates all five routes (GET/PUT/DELETE /config + POST /send + POST
 * /upload-media).
 *
 * Error envelope contract from Plan 14-02/14-04 is `{ error, code, details }`.
 * Errors are surfaced as `TwitterApiError` instances with a `.code` property
 * attached so UI code can pattern-match (e.g. `TWITTER_OAUTH1_REQUIRED` shows
 * the configure-OAuth1 hint; `INTEGRATION_NOT_CONFIGURED` shows the
 * configure-first prompt).
 *
 * Pattern mirrors `discordApi.ts` (Plan 14-03) verbatim — same fetch shape,
 * same envelope decoding — with one addition: `uploadMedia(File)` uses
 * multipart FormData (field name 'media') because the backend expects
 * binary bytes, not JSON.
 */

import { apiRequest } from '@/lib/api-client'

import {
  MediaUploadResultSchema,
  TweetResultSchema,
  TwitterConfigSchema,
  type MediaUploadResult,
  type SendTweetBody,
  type TweetResult,
  type TwitterConfig,
  type TwitterCredsInput,
} from './types'

const BASE = '/api/cms/admin/integrations/twitter'
const FETCH_OPTS: RequestInit = { credentials: 'include' }

/**
 * Error class carrying the typed `code` + `details` from the Plan 14-02
 * error envelope. The page handler can `instanceof TwitterApiError` and
 * branch on `err.code` (e.g. show OAuth1.0a hint when
 * `TWITTER_OAUTH1_REQUIRED`).
 */
export class TwitterApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly details: unknown
  constructor(
    message: string,
    code: string,
    status: number,
    details: unknown = null,
  ) {
    super(message)
    this.name = 'TwitterApiError'
    this.code = code
    this.status = status
    this.details = details
    // Restore prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, TwitterApiError.prototype)
  }
}

async function throwFromResponse(response: Response): Promise<never> {
  const data: { error?: string; code?: string; details?: unknown } =
    await response.json().catch(() => ({}))
  const message = data.error || `Request failed (${response.status})`
  const code = data.code || `HTTP_${response.status}`
  throw new TwitterApiError(message, code, response.status, data.details)
}

export const twitterApi = {
  /**
   * GET /api/cms/admin/integrations/twitter/config — returns the masked
   * connection state. `connected: false` + null/false fields when no row
   * exists.
   */
  async getConfig(): Promise<TwitterConfig> {
    const res = await apiRequest(`${BASE}/config`, FETCH_OPTS)
    if (!res.ok) await throwFromResponse(res)
    return TwitterConfigSchema.parse(await res.json())
  },

  /**
   * PUT /api/cms/admin/integrations/twitter/config — saves a new (or
   * replaces existing) Twitter creds row. Server validates that the bearer
   * token is non-empty and (if oauth1 present) all four sub-fields are
   * non-empty before encrypting and storing.
   */
  async putConfig(body: TwitterCredsInput): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * DELETE /api/cms/admin/integrations/twitter/config — hard-delete (no
   * soft-delete column per Plan 14-02 decision, mirrored in 14-04). To
   * "undelete" the admin just re-PUTs the same creds.
   */
  async deleteConfig(): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'DELETE',
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * POST /api/cms/admin/integrations/twitter/send — posts a tweet via X v2
   * bearer-token API. Server enforces 1..=280 chars; the client mirrors
   * the empty-text check to avoid a doomed network round-trip but defers
   * the over-limit check to the composer (since 281+ disables the Send
   * button at the UI level).
   *
   * Returns the typed `TweetResult { id, text, url }` so the caller can
   * toast a link to the published tweet.
   */
  async sendTweet(body: SendTweetBody): Promise<TweetResult> {
    if ((body.text?.trim() ?? '').length === 0) {
      throw new Error('text is required')
    }
    const res = await apiRequest(`${BASE}/send`, {
      ...FETCH_OPTS,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwFromResponse(res)
    return TweetResultSchema.parse(await res.json())
  },

  /**
   * POST /api/cms/admin/integrations/twitter/upload-media — multipart upload
   * to X v1.1 media endpoint via the sidecar. Field name MUST be 'media'
   * (Plan 14-04's `upload_twitter_media` handler reads that key).
   *
   * Server caps payload at 5 MB and requires OAuth1.0a sub-creds on the
   * row — without them, returns 503 + `TWITTER_OAUTH1_REQUIRED` which the
   * caller surfaces via the typed error code.
   */
  async uploadMedia(file: File): Promise<MediaUploadResult> {
    const fd = new FormData()
    fd.append('media', file)
    const res = await apiRequest(`${BASE}/upload-media`, {
      ...FETCH_OPTS,
      method: 'POST',
      body: fd,
    })
    if (!res.ok) await throwFromResponse(res)
    return MediaUploadResultSchema.parse(await res.json())
  },
} as const

export type TwitterApi = typeof twitterApi
